import { useController, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "ds/components/Box";
import Banner from "ds/components/Banner";
import useDefaultRoleAssignmentMethods from "components/AssignRoleForm/useDefaultRoleAssignmentMethods";
import AssignRoleForm from "components/AssignRoleForm";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";
import usePrevious from "hooks/usePrevious";

type FormFieldRoleAssignmentProps<T extends FieldValues = FieldValues> = {
  name: Path<T>;
  availableRoles: Array<{ id: string; name: string }>;
  loading?: boolean;
  required?: boolean;
  setHasDirtyFields: (hasUnfinishedChanges: boolean) => void;
  analyticsPage?: AnalyticsPage;
};

const FormFieldRoleAssignment = <T extends FieldValues = FieldValues>({
  name,
  availableRoles,
  loading = false,
  required = false,
  setHasDirtyFields,
  analyticsPage,
}: FormFieldRoleAssignmentProps<T>) => {
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  const hasDirtyFields = useMemo(() => Object.values(dirtyFields).some(Boolean), [dirtyFields]);

  const { control, formState, trigger } = useFormContext();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const { field, fieldState } = useController({
    name,
    control,
    defaultValue: [] as PathValue<T, Path<T>>,
    rules: {
      validate: (value) => {
        // Check for uncommitted changes if form has dirty fields
        if (hasDirtyFields) {
          return 'You have unsaved role selections. Please click "Add" to save them or clear the fields.';
        }

        // Check if field is required and empty
        if (required && (!value || value.length === 0)) {
          return "At least one role assignment is required";
        }

        return true;
      },
    },
  });

  const { onChange } = field;

  const { onDelete, onCreate, onEdit, assignedRoles } = useDefaultRoleAssignmentMethods();

  const prevAssignedRoles = useRef(assignedRoles);
  const prevSubmitCount = usePrevious(formState.submitCount);

  useEffect(() => {
    if (formState.submitCount > 0) {
      trigger(name);
    }
  }, [formState.submitCount, hasDirtyFields, trigger, name]);

  // Track analytics when form is submitted with uncommitted changes
  useEffect(() => {
    if (
      prevSubmitCount !== undefined &&
      formState.submitCount > prevSubmitCount &&
      hasDirtyFields &&
      analyticsPage
    ) {
      trackSegmentAnalyticsEvent("Role Assignment Skipped Add Button");
    }
  }, [
    formState.submitCount,
    prevSubmitCount,
    hasDirtyFields,
    analyticsPage,
    trackSegmentAnalyticsEvent,
  ]);

  useEffect(() => {
    setHasDirtyFields(hasDirtyFields);
  }, [hasDirtyFields, setHasDirtyFields]);

  // Sync assignedRoles to form when they actually change
  useEffect(() => {
    if (prevAssignedRoles.current !== assignedRoles) {
      onChange(assignedRoles);
      prevAssignedRoles.current = assignedRoles;
    }
  }, [assignedRoles, onChange]);

  const handleDirtyFields = useCallback((formId: string, value: boolean) => {
    setDirtyFields((prev) => ({ ...prev, [formId]: value }));
  }, []);

  return (
    <Box direction="column" gap="large" fullWidth>
      {fieldState.error && (
        <Banner variant="danger" title="Role needs to be attached">
          {fieldState.error.message}
        </Banner>
      )}
      <AssignRoleForm
        onCreate={onCreate}
        onEdit={onEdit}
        onDelete={onDelete}
        setDirtyFields={handleDirtyFields}
        availableRoles={availableRoles}
        assignedRoles={assignedRoles}
        loading={loading}
      />
    </Box>
  );
};

export default FormFieldRoleAssignment;
